import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Sparkles, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';

interface AITaskProcessingDialogProps {
  isOpen: boolean;
  fileName: string;
  onClose: () => void;
  onCancel?: () => void;
}

export function AITaskProcessingDialog({
  isOpen,
  fileName,
  onClose,
}: AITaskProcessingDialogProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [showNavigationPrompt, setShowNavigationPrompt] = useState(false);
  const [nextLocation, setNextLocation] = useState<Location | null>(null);

  // Block navigation when dialog is open
  useEffect(() => {
    if (!isOpen) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'AI task generation is in progress. Are you sure you want to leave?';
      return e.returnValue;
    };

    const handlePopState = (e: PopStateEvent) => {
      if (!window.confirm('AI task generation is in progress. Are you sure you want to leave? This may interrupt the process.')) {
        // Prevent navigation
        window.history.pushState(null, '', window.location.pathname);
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    // Store the original pushState
    const originalPushState = window.history.pushState;
    window.history.pushState = function(...args) {
      if (isOpen) {
        if (!window.confirm('AI task generation is in progress. Are you sure you want to leave? This may interrupt the process.')) {
          return;
        }
      }
      return originalPushState.apply(window.history, args);
    };

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      window.history.pushState = originalPushState;
    };
  }, [isOpen]);
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px] bg-card" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <DialogTitle>Processing Document</DialogTitle>
          </div>
        </DialogHeader>
        
        <div className="py-4 space-y-6">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="relative">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <Sparkles className="absolute top-1/2 left-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 text-primary" />
            </div>
            
            <div className="text-center space-y-2">
              <h3 className="text-lg font-medium">Analysing {fileName}</h3>
              <p className="text-sm text-muted-foreground">
              Processing document and comparing commitments to existing assurance items. New assurance suggestions will be available after processing in System Updates.
              </p>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3 mt-4">
                <div className="flex items-center justify-center gap-2 text-yellow-700 dark:text-yellow-300">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm font-medium">Please do not leave or close this page until processing is complete.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
